import React, { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useNotification } from 'src/contexts/NotificationContext';
import { CampaignProvider } from './CampaignContext';
import { CrmSyncProvider } from './CrmSyncContext';
import { FieldMappingProvider } from './FieldMappingContext';
import { ContactsProvider } from './ContactsContext';

interface CrmSyncProvidersProps {
  children: ReactNode;
}

function CrmSyncErrorFallback({
  resetErrorBoundary,
}: {
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">
          Something went wrong
        </h2>
        <p className="text-neutral-600 mb-4">
          There was an error loading contacts from your CRM. Please try again or
          contact support if the problem persists.
        </p>
        <button
          onClick={resetErrorBoundary}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

export const CrmSyncProviders: React.FC<CrmSyncProvidersProps> = ({
  children,
}) => {
  const { track } = useAnalytics();
  const notification = useNotification();

  const handleError = (error: Error, errorInfo: { componentStack: string }) => {
    // Track the error for analytics
    track.errorExperienced({
      error: 'crm_sync_component_error',
      message: error.message,
    });

    // Show notification to user
    notification.error(
      'An error occurred while adding contacts from your CRM. Please try refreshing the page.',
    );

    // Log to console for debugging
    console.error('CRM Sync Error:', error, errorInfo);
  };

  return (
    <ErrorBoundary
      FallbackComponent={CrmSyncErrorFallback}
      onError={handleError}
      onReset={() => {
        // Clear any error state if needed
        window.location.reload();
      }}
    >
      <CampaignProvider>
        <ErrorBoundary
          FallbackComponent={({ resetErrorBoundary }) => (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-amber-800">
                Failed to load CRM sync configuration.
                <button
                  onClick={resetErrorBoundary}
                  className="ml-2 text-amber-600 underline hover:text-amber-800"
                >
                  Try again
                </button>
              </p>
            </div>
          )}
          onError={(error) => {
            track.errorExperienced({
              error: 'crm_sync_configuration_error',
              message: error.message,
            });
          }}
        >
          <CrmSyncProvider>
            <ErrorBoundary
              FallbackComponent={({ resetErrorBoundary }) => (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-amber-800">
                    Failed to load field mapping.
                    <button
                      onClick={resetErrorBoundary}
                      className="ml-2 text-amber-600 underline hover:text-amber-800"
                    >
                      Try again
                    </button>
                  </p>
                </div>
              )}
              onError={(error) => {
                track.errorExperienced({
                  error: 'field_mapping_error',
                  message: error.message,
                });
              }}
            >
              <FieldMappingProvider>
                <ErrorBoundary
                  FallbackComponent={({ resetErrorBoundary }) => (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-amber-800">
                        Failed to load contacts.
                        <button
                          onClick={resetErrorBoundary}
                          className="ml-2 text-amber-600 underline hover:text-amber-800"
                        >
                          Try again
                        </button>
                      </p>
                    </div>
                  )}
                  onError={(error) => {
                    track.errorExperienced({
                      error: 'contacts_provider_error',
                      message: error.message,
                    });
                  }}
                >
                  <ContactsProvider>{children}</ContactsProvider>
                </ErrorBoundary>
              </FieldMappingProvider>
            </ErrorBoundary>
          </CrmSyncProvider>
        </ErrorBoundary>
      </CampaignProvider>
    </ErrorBoundary>
  );
};
